package top.wcpe.allincore.ui.runtime.host

import top.wcpe.allincore.ui.runtime.internal.concurrent.AtomicBooleanFlag
import top.wcpe.allincore.ui.runtime.ir.request.SuspendContinuation
import top.wcpe.allincore.ui.runtime.ir.state.PendingAwaitable
import kotlin.jvm.JvmStatic

/**
 * Timer 异步任务的 awaitable 实现。
 *
 * 两种构造形态：
 * - `delayAsync(ms)`：[ofDelay]，[onResume] 时调度一个 `timer.timeout` 延迟 ms，结果 = 延迟值。
 * - `compute(lambda)`：[ofCompute]，[onResume] 立即调度（delay=0），结果 = lambda 求值结果。
 *
 * cancel 语义：调用方（如 session dispose）触发 cancel 时，已挂起的 continuation 不会被 resume；
 * 配合 [TimerHost.dispose] 一起使用即可清理所有未完成 awaitable。
 */
class TimerAwaitable private constructor(
    private val host: TimerHost,
    delayMs: Long,
    private val bodySupplier: UiSupplier<Any?>?,
    private val delayResult: Any?,
) : PendingAwaitable {

    private val delayMs: Long = maxOf(0L, delayMs)
    private val cancelled = AtomicBooleanFlag(false)
    private val resumed = AtomicBooleanFlag(false)
    private var handle: TimerHandle? = null

    override fun onResume(cont: SuspendContinuation) {
        if (cancelled.get()) {
            // 已取消：立即 resume(null) 让 await 不至于永远挂起；与 await 取消语义一致
            cont.resume(null)
            return
        }
        handle = host.timeout(delayMs) {
            if (cancelled.get()) {
                return@timeout
            }
            if (!resumed.compareAndSet(false, true)) {
                return@timeout
            }
            val result = try {
                if (bodySupplier != null) bodySupplier.get() else delayResult
            } catch (t: Throwable) {
                // body 执行失败：把异常 wrap 成 null，避免 continuation 永久挂起；异常本身已由 TimerHost 记日志
                null
            }
            cont.resume(result)
        }
    }

    override fun cancel() {
        if (cancelled.compareAndSet(false, true)) {
            handle?.cancel()
        }
    }

    override fun isCancelled(): Boolean = cancelled.get()

    companion object {
        /** delayAsync 构造：`await timer.delayAsync(ms)` → ms 后 resume(ms)。 */
        @JvmStatic
        fun ofDelay(host: TimerHost, ms: Long): TimerAwaitable =
            TimerAwaitable(host, ms, bodySupplier = null, delayResult = ms.toDouble())

        /**
         * compute 构造：`await timer.compute { body }` → 一个调度周期后执行 body，结果作为 await 返回值。
         * 本期只在 DSL 单线程异步（非真后台线程），避免跨线程访问 state。
         */
        @JvmStatic
        fun ofCompute(host: TimerHost, body: UiSupplier<Any?>): TimerAwaitable =
            TimerAwaitable(host, 0L, body, delayResult = null)
    }
}
